import {
  signInWithPopup,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';

const AUTH_DEBUG_STORAGE_KEY = 'physioVideoHubAuthDebug';
const AUTH_DEBUG_ENABLED =
  typeof window !== 'undefined' &&
  (
    window.location.search.includes('authDebug=1') ||
    window.localStorage.getItem('authDebug') === '1'
  );

const sanitizeError = (error) => {
  if (!error) return null;

  return {
    code: error.code || '',
    message: error.message || String(error),
    customData: error.customData || null
  };
};

const buildAuthDebugSnapshot = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  return {
    href: window.location.href,
    origin: window.location.origin,
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    hash: window.location.hash,
    search: window.location.search
  };
};

const collectMatchingStorageEntries = (storage) => {
  if (!storage) return [];

  const entries = [];
  const matchers = ['firebase', 'auth', 'redirect'];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (!key) continue;

    const lowerKey = key.toLowerCase();
    if (!matchers.some((matcher) => lowerKey.includes(matcher))) {
      continue;
    }

    let value = '';

    try {
      value = storage.getItem(key) || '';
    } catch (error) {
      value = `[unreadable: ${error.message || String(error)}]`;
    }

    entries.push({
      key,
      preview: value.slice(0, 240)
    });
  }

  return entries;
};

const getFirebaseRedirectStorageDebug = () => {
  if (typeof window === 'undefined') {
    return {
      sessionStorageEntries: [],
      localStorageEntries: []
    };
  }

  return {
    sessionStorageEntries: collectMatchingStorageEntries(window.sessionStorage),
    localStorageEntries: collectMatchingStorageEntries(window.localStorage)
  };
};

const readAuthDebugEvents = () => {
  if (typeof window === 'undefined') return [];

  try {
    return JSON.parse(window.sessionStorage.getItem(AUTH_DEBUG_STORAGE_KEY) || '[]');
  } catch (error) {
    console.warn('[auth-debug] Failed to parse stored auth events:', error);
    return [];
  }
};

const writeAuthDebugEvents = (events) => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(AUTH_DEBUG_STORAGE_KEY, JSON.stringify(events.slice(-50)));
  } catch (error) {
    console.warn('[auth-debug] Failed to store auth events:', error);
  }
};

const authDebugLog = (event, details = {}) => {
  const entry = {
    event,
    timestamp: new Date().toISOString(),
    ...buildAuthDebugSnapshot(),
    ...details
  };

  const events = [...readAuthDebugEvents(), entry];
  writeAuthDebugEvents(events);

  if (typeof window !== 'undefined') {
    window.__PHYSIO_AUTH_DEBUG__ = events;
  }

  if (AUTH_DEBUG_ENABLED) {
    console.log('[auth-debug]', entry);
  }

  return entry;
};

export const authService = {
  isDebugEnabled() {
    return AUTH_DEBUG_ENABLED;
  },

  getDebugEvents() {
    return readAuthDebugEvents();
  },

  clearDebugEvents() {
    writeAuthDebugEvents([]);
    if (typeof window !== 'undefined') {
      window.__PHYSIO_AUTH_DEBUG__ = [];
    }
  },

  async handleRedirectResult() {
    try {
      authDebugLog('handleRedirectResult:start', {
        currentUserEmail: auth.currentUser?.email || '',
        hasCurrentUser: Boolean(auth.currentUser),
        redirectStorage: getFirebaseRedirectStorageDebug()
      });

      const result = await getRedirectResult(auth);

      authDebugLog('handleRedirectResult:success', {
        hasResult: Boolean(result),
        hasUser: Boolean(result?.user),
        resultUserEmail: result?.user?.email || '',
        providerId: result?.providerId || '',
        redirectStorage: getFirebaseRedirectStorageDebug()
      });

      return result;
    } catch (error) {
      authDebugLog('handleRedirectResult:error', {
        error: sanitizeError(error),
        redirectStorage: getFirebaseRedirectStorageDebug()
      });
      console.error('Redirect sign-in error:', error);
      throw error;
    }
  },

  async getAuthorizedUserProfile(email) {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    
    if (!normalizedEmail) {
      authDebugLog('authorization:missing-email');
      return {
        ok: false,
        reason: 'missing-email',
        profile: null,
        error: ''
      };
    }

    try {
      authDebugLog('authorization:lookup:start', {
        email: normalizedEmail
      });

      const usersRef = collection(db, 'authorizedUsers');
      const usersQuery = query(usersRef, where('email', '==', normalizedEmail));
      const snapshot = await getDocs(usersQuery);

      if (snapshot.empty) {
        authDebugLog('authorization:lookup:not-found', {
          email: normalizedEmail
        });
        return {
          ok: false,
          reason: 'not-found',
          profile: null,
          error: ''
        };
      }

      const userProfile = snapshot.docs[0].data();

      if (userProfile.active !== true) {
        authDebugLog('authorization:lookup:inactive', {
          email: normalizedEmail,
          active: userProfile.active
        });
        return {
          ok: false,
          reason: 'inactive',
          profile: null,
          error: ''
        };
      }

      authDebugLog('authorization:lookup:matched', {
        email: normalizedEmail,
        role: userProfile.role || ''
      });

      return {
        ok: true,
        reason: 'matched',
        profile: {
          ...userProfile,
          email: normalizedEmail
        },
        error: ''
      };
    } catch (error) {
      authDebugLog('authorization:lookup:error', {
        email: normalizedEmail,
        error: sanitizeError(error)
      });
      console.error('Error checking authorization:', error);
      return {
        ok: false,
        reason: error?.code === 'permission-denied' ? 'permission-denied' : 'lookup-error',
        profile: null,
        error: error?.message || String(error)
      };
    }
  },

  async isAuthorized(email) {
    const result = await this.getAuthorizedUserProfile(email);
    return result.ok;
  },

  async signInWithGoogle() {
    try {
      authDebugLog('signInWithGoogle:start', {
        flow: 'popup',
        authDomain: auth.config?.authDomain || '',
        currentUserEmail: auth.currentUser?.email || '',
        redirectStorage: getFirebaseRedirectStorageDebug()
      });

      const popupResult = await signInWithPopup(auth, googleProvider);
      authDebugLog('signInWithGoogle:popup-success', {
        userEmail: popupResult?.user?.email || '',
        providerId: popupResult?.providerId || ''
      });
      return popupResult;
    } catch (error) {
      authDebugLog('signInWithGoogle:error', {
        error: sanitizeError(error),
        redirectStorage: getFirebaseRedirectStorageDebug()
      });
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in cancelled');
      }
      throw error;
    }
  },

  async signOut() {
    authDebugLog('signOut:start', {
      currentUserEmail: auth.currentUser?.email || ''
    });
    await firebaseSignOut(auth);
    authDebugLog('signOut:success');
  },
  onAuthStateChange(callback) {
    authDebugLog('authState:listener-attached');

    return onAuthStateChanged(auth, (currentUser) => {
      authDebugLog('authState:changed', {
        hasUser: Boolean(currentUser),
        currentUserEmail: currentUser?.email || '',
        emailVerified: currentUser?.emailVerified ?? null,
        providerIds: currentUser?.providerData?.map((provider) => provider?.providerId).filter(Boolean) || []
      });
      callback(currentUser);
    });
  }
  
  // async getAuthInstance() { return auth }
};
